import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Skeleton,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Backdrop,
  Collapse,
  Tooltip,
  Divider,
} from "@mui/material";
import { ResponsiveNavbar } from "../../../components/ResponsiveNavbar/ResponsiveNavbar";
import jessProfileIcon from "../../../assets/navbar/iconperfiljesss.svg";
import jessProfileIconOff from "../../../assets/navbar/iconperfiljesssOff.svg";
import jessNameLogo from "../../../assets/navbar/nomejesss.svg";
import {
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Launch as LaunchIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Extension as ExtensionIcon,
  Webhook as WebhookIcon,
  ExpandMore as ExpandMoreIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Autorenew as AutorenewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import {
  getConnectorsWithStatus,
  initiateOAuthFlow,
  testConnection as apiTestConnection,
  disconnectConnection as apiDisconnectConnection,
  getWebhookSubscriptions,
  pauseWebhookSubscription,
  resumeWebhookSubscription,
  renewWebhookSubscription,
  deleteWebhookSubscription,
  OAuthConnector,
  OAuthConnection,
  WebhookSubscription,
  getWebhookStatusColor,
  formatWebhookEventType,
  selectAtlassianSite,
} from "../../../api/oauth";
import {
  fetchAgentConnectionStatus,
  AgentConnectionStatus,
} from "../../../api/agent";
import { logger } from "../../../utils/logger";
import { useGlobalTheme } from "../../../context/GlobalThemeContext";
import { getLoggedinUser } from "../../../api/apiCore";
import { formatLocaleDate } from "../../../utils/dateutils";

interface ConnectorWithStatus extends OAuthConnector {
  isConnected: boolean;
  connection?: OAuthConnection;
}

// OAuth flow states
type OAuthFlowState = "idle" | "authorizing" | "waiting" | "success" | "error";

const ConnectionsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isDarkTheme = useGlobalTheme();

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    localStorage.setItem("theme", newTheme ? "dark" : "light");
    window.dispatchEvent(
      new CustomEvent("appThemeChange", { detail: { isDark: newTheme } }),
    );
  };

  // Dark mode derived values
  const isDark = isDarkTheme;
  const cardBg = isDark ? "rgba(30, 30, 30, 0.95)" : "white";
  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : "none";
  const textPrimary = isDark ? "#ffffff" : "#333";
  const textSecondary = isDark ? "#a0a0a0" : "#666";
  const textMuted = isDark ? "#888" : "#999";
  const dialogBg = isDark ? "rgba(20, 20, 20, 0.98)" : undefined;
  const dialogBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : undefined;

  const [agentConnectionStatus, setAgentConnectionStatus] =
    useState<AgentConnectionStatus | null>(null);

  // Function to check agent connection status
  const checkAgentConnectionStatus = useCallback(async () => {
    try {
      const status = await fetchAgentConnectionStatus();
      setAgentConnectionStatus(status);
    } catch (error) {
      logger.error("Error fetching agent connection status:", error);
      const loggedUser = await getLoggedinUser();
      const fallbackStatus: AgentConnectionStatus = {
        isAgentConnected: false,
        companyId: loggedUser?.companyId || "",
        timestamp: Date.now(),
      };
      setAgentConnectionStatus(fallbackStatus);
    }
  }, []);

  // Effect to check agent status on mount and periodically
  useEffect(() => {
    checkAgentConnectionStatus();
    const intervalId = setInterval(() => {
      checkAgentConnectionStatus();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [checkAgentConnectionStatus]);

  // State
  const [connectors, setConnectors] = useState<ConnectorWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionModal, setConnectionModal] = useState<{
    open: boolean;
    connector: ConnectorWithStatus | null;
  }>({ open: false, connector: null });
  const [oauthFlow, setOauthFlow] = useState<{
    state: OAuthFlowState;
    connector: ConnectorWithStatus | null;
    error?: string;
  }>({ state: "idle", connector: null });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConnection, setSelectedConnection] =
    useState<ConnectorWithStatus | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [testingConnection, setTestingConnection] = useState<string | null>(
    null,
  );

  // Atlassian site selection state
  const [siteSelectionDialog, setSiteSelectionDialog] = useState<{
    open: boolean;
    connectionId: string;
    resources: Array<{ id: string; name: string; url: string }>;
    loading: boolean;
  }>({ open: false, connectionId: "", resources: [], loading: false });

  // Webhook state
  const [webhookSubscriptions, setWebhookSubscriptions] = useState<
    Record<string, WebhookSubscription[]>
  >({});
  const [expandedWebhooks, setExpandedWebhooks] = useState<
    Record<string, boolean>
  >({});
  const [loadingWebhooks, setLoadingWebhooks] = useState<
    Record<string, boolean>
  >({});

  // Refs
  const popupRef = useRef<Window | null>(null);
  const popupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbackHandledRef = useRef<boolean>(false);

  const fetchConnectorsWithStatus = async (): Promise<
    ConnectorWithStatus[]
  > => {
    return await getConnectorsWithStatus();
  };

  const initiateOAuth = async (
    connectorId: string,
    forceAccountSelection: boolean = false,
  ): Promise<{ authorization_url: string; state: string }> => {
    // Build params object according to new API spec:
    // - OAuth parameters (prompt, etc.) go in request body
    // - redirect_uri goes in query string
    const params: { prompt?: string; redirect_uri?: string } = {};

    // Force account selection when adding another connection
    // This will make OAuth providers (like Google) show the account picker
    if (forceAccountSelection) {
      params.prompt = "select_account";
    }

    // Use the updated API function that handles request body correctly
    return await initiateOAuthFlow(connectorId, params);
  };

  const testConnection = async (
    connectorId: string,
  ): Promise<{ success: boolean; data?: any; error?: string }> => {
    return await apiTestConnection(connectorId);
  };

  const disconnectConnector = async (connectorId: string): Promise<void> => {
    await apiDisconnectConnection(connectorId);

    // Update local state
    setConnectors((prev) =>
      prev.map((conn) =>
        conn.id === connectorId
          ? { ...conn, isConnected: false, connection: undefined }
          : conn,
      ),
    );
  };

  // Check if a connection needs Atlassian site selection
  const checkAtlassianSiteSelection = useCallback(
    (updatedConnectors: ConnectorWithStatus[]) => {
      for (const connector of updatedConnectors) {
        if (
          connector.connection?.metadata?.accessible_resources &&
          !connector.connection?.metadata?.cloud_id &&
          (connector.provider === "atlassian_jira" ||
            connector.provider === "atlassian_confluence" ||
            connector.provider === "atlassian")
        ) {
          const resources = connector.connection.metadata
            .accessible_resources as Array<{
            id: string;
            name: string;
            url: string;
          }>;
          if (resources.length > 0) {
            setSiteSelectionDialog({
              open: true,
              connectionId: connector.connection.id,
              resources,
              loading: false,
            });
            return;
          }
        }
      }
    },
    [],
  );

  const handleSelectSite = async (cloudId: string) => {
    setSiteSelectionDialog((prev) => ({ ...prev, loading: true }));
    try {
      await selectAtlassianSite(siteSelectionDialog.connectionId, cloudId);
      setSiteSelectionDialog({
        open: false,
        connectionId: "",
        resources: [],
        loading: false,
      });
      // Refresh connectors to get updated metadata
      const updatedConnectors = await fetchConnectorsWithStatus();
      setConnectors(updatedConnectors);
      setSnackbar({
        open: true,
        message:
          t("oauth.connections.site_selected") || "Site selected successfully",
        severity: "success",
      });
    } catch (error) {
      logger.error("Failed to select Atlassian site:", error);
      setSiteSelectionDialog((prev) => ({ ...prev, loading: false }));
      setSnackbar({
        open: true,
        message:
          t("oauth.connections.site_selection_failed") ||
          "Failed to select site",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    fetchConnectorsWithStatus()
      .then((data) => {
        setConnectors(data);
        checkAtlassianSiteSelection(data);
      })
      .catch((error) => {
        logger.error("Failed to fetch connectors:", error);
        setSnackbar({
          open: true,
          message: t("oauth.connections.failed_load"),
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen for postMessage from OAuth callback popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify message structure and state for security
      // Note: We allow cross-origin messages from the callback page (ngrok)
      // but verify the state parameter matches what we stored
      if (event.data?.type !== "oauth_callback") {
        return;
      }

      // Handle JSON response from backend (new format)
      if (event.data.jsonResponse) {
        const { jsonResponse } = event.data;

        // Mark callback as handled to prevent duplicate processing
        callbackHandledRef.current = true;

        // Clear interval if still running
        if (popupIntervalRef.current) {
          clearInterval(popupIntervalRef.current);
          popupIntervalRef.current = null;
        }

        // Close popup if still open
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }

        if (jsonResponse.error) {
          // Error response from backend
          setOauthFlow({
            state: "error",
            connector: null,
            error: jsonResponse.error,
          });
          setSnackbar({
            open: true,
            message: jsonResponse.error || t("oauth.authorization_failed"),
            severity: "error",
          });
        } else if (jsonResponse.connection || jsonResponse.message) {
          // Success response from backend
          setOauthFlow({ state: "success", connector: null });
          fetchConnectorsWithStatus()
            .then((updatedConnectors) => {
              setConnectors(updatedConnectors);
              setOauthFlow({ state: "idle", connector: null });
              // Check if Atlassian site selection is needed
              checkAtlassianSiteSelection(updatedConnectors);
              setSnackbar({
                open: true,
                message:
                  jsonResponse.message ||
                  t("oauth.connection_established_success"),
                severity: "success",
              });
            })
            .catch((err) => {
              logger.error("Failed to refresh connectors:", err);
              setOauthFlow({ state: "idle", connector: null });
              // Still show success even if refresh fails
              setSnackbar({
                open: true,
                message:
                  jsonResponse.message ||
                  t("oauth.connection_established_success"),
                severity: "success",
              });
            });
        } else {
          setOauthFlow({
            state: "error",
            connector: null,
            error: t("oauth.invalid_callback_parameters"),
          });
        }

        // Clean up stored values
        localStorage.removeItem("oauth_state");
        localStorage.removeItem("oauth_connector_id");
        return;
      }

      // Handle URL parameter-based callback (original flow)
      const { code, state, error } = event.data;
      const storedState = localStorage.getItem("oauth_state");
      const connectorId = localStorage.getItem("oauth_connector_id");

      // Verify state matches (security check)
      if (!error && state !== storedState) {
        return;
      }

      // Mark callback as handled to prevent duplicate processing
      callbackHandledRef.current = true;

      // Clear interval if still running
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
        popupIntervalRef.current = null;
      }

      // Close popup if still open
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }

      if (error) {
        setOauthFlow({ state: "error", connector: null, error: error });
        setSnackbar({
          open: true,
          message: t("oauth.authorization_failed"),
          severity: "error",
        });
      } else if (code && state && state === storedState && connectorId) {
        // Success - refetch connectors to get real connection data
        setOauthFlow({ state: "success", connector: null });
        fetchConnectorsWithStatus()
          .then((updatedConnectors) => {
            setConnectors(updatedConnectors);
            setOauthFlow({ state: "idle", connector: null });
            // Check if Atlassian site selection is needed
            checkAtlassianSiteSelection(updatedConnectors);
            setSnackbar({
              open: true,
              message: t("oauth.connection_established_success"),
              severity: "success",
            });
          })
          .catch((err) => {
            logger.error("Failed to refresh connectors:", err);
            setOauthFlow({ state: "idle", connector: null });
            // Still show success even if refresh fails
            setSnackbar({
              open: true,
              message: t("oauth.connection_established_success"),
              severity: "success",
            });
          });
      } else {
        setOauthFlow({
          state: "error",
          connector: null,
          error: t("oauth.invalid_callback_parameters"),
        });
      }

      // Clean up stored values
      localStorage.removeItem("oauth_state");
      localStorage.removeItem("oauth_connector_id");
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [t]);

  // Cleanup popup on unmount
  useEffect(() => {
    return () => {
      if (popupRef.current) {
        popupRef.current.close();
      }
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
      }
    };
  }, []);

  const getConnectionDisplayName = (connector: ConnectorWithStatus): string => {
    if (!connector.connection?.metadata) {
      return connector.name;
    }

    const { metadata } = connector.connection;
    if (metadata.workspace_name) {
      return `${connector.name} (${metadata.workspace_name})`;
    }
    if (metadata.email) {
      return `${connector.name} (${metadata.email})`;
    }
    if (metadata.user_name) {
      return `${connector.name} (${metadata.user_name})`;
    }

    return connector.name;
  };

  const handleConnect = (connector: ConnectorWithStatus) => {
    setConnectionModal({ open: true, connector });
  };

  const handleAddAnotherConnection = async (connector: ConnectorWithStatus) => {
    // Start OAuth flow directly without showing the modal
    setOauthFlow({ state: "authorizing", connector });

    try {
      // Force account selection when adding another connection
      const { authorization_url, state } = await initiateOAuth(
        connector.id,
        true,
      );

      // Store state for verification
      localStorage.setItem("oauth_state", state);
      localStorage.setItem("oauth_connector_id", connector.id);

      // Open popup
      const popup = window.open(
        authorization_url,
        "oauth_popup",
        "width=500,height=600,scrollbars=yes,resizable=yes",
      );

      if (!popup) {
        throw new Error(t("oauth.popup_blocked"));
      }

      popupRef.current = popup;
      setOauthFlow((prev) => ({ ...prev, state: "waiting" }));

      // Reset callback handled flag for new auth flow
      callbackHandledRef.current = false;

      // Monitor popup closure - but don't immediately show error
      // The postMessage handler will handle success/error
      popupIntervalRef.current = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupIntervalRef.current!);
          popupIntervalRef.current = null;

          // If callback was already handled via postMessage, don't do anything
          if (callbackHandledRef.current) {
            return;
          }

          // Check if connection was actually established by refetching
          const checkConnectionStatus = async () => {
            try {
              const updatedConnectors = await fetchConnectorsWithStatus();
              const updatedConnector = updatedConnectors.find(
                (c) => c.id === connector.id,
              );

              if (updatedConnector?.isConnected) {
                // Connection was successful, just update the UI
                setConnectors(updatedConnectors);
                setOauthFlow({ state: "idle", connector: null });
                setSnackbar({
                  open: true,
                  message: t("oauth.connection_established_success"),
                  severity: "success",
                });
                callbackHandledRef.current = true;
              } else {
                // Popup was closed without completing authorization
                setOauthFlow({
                  state: "error",
                  connector: null,
                  error: t("oauth.authorization_cancelled"),
                });
              }
            } catch (err) {
              // If we can't check, assume it was cancelled
              setOauthFlow({
                state: "error",
                connector: null,
                error: t("oauth.authorization_cancelled"),
              });
            }
          };

          // Small delay to allow postMessage to arrive first (if it's coming)
          // But also check immediately in case backend returned JSON directly
          checkConnectionStatus();
          // Also check again after a short delay in case postMessage arrives late
          setTimeout(checkConnectionStatus, 300);
        }
      }, 1000);
    } catch (error) {
      setOauthFlow({
        state: "error",
        connector: null,
        error:
          error instanceof Error
            ? error.message
            : t("oauth.authorization_failed"),
      });
    }
  };

  const handleStartAuth = async () => {
    if (!connectionModal.connector) return;

    setOauthFlow({
      state: "authorizing",
      connector: connectionModal.connector,
    });
    setConnectionModal({ open: false, connector: null });

    try {
      const { authorization_url, state } = await initiateOAuth(
        connectionModal.connector.id,
      );

      // Store state for verification
      localStorage.setItem("oauth_state", state);
      localStorage.setItem("oauth_connector_id", connectionModal.connector.id);

      // Open popup
      const popup = window.open(
        authorization_url,
        "oauth_popup",
        "width=500,height=600,scrollbars=yes,resizable=yes",
      );

      if (!popup) {
        throw new Error(t("oauth.popup_blocked"));
      }

      popupRef.current = popup;
      setOauthFlow((prev) => ({ ...prev, state: "waiting" }));

      // Reset callback handled flag for new auth flow
      callbackHandledRef.current = false;

      // Monitor popup closure - but don't immediately show error
      // The postMessage handler will handle success/error
      popupIntervalRef.current = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupIntervalRef.current!);
          popupIntervalRef.current = null;

          // If callback was already handled via postMessage, don't do anything
          if (callbackHandledRef.current) {
            return;
          }

          // Check if connection was actually established by refetching
          const checkConnectionStatus = async () => {
            try {
              const updatedConnectors = await fetchConnectorsWithStatus();
              const connector = updatedConnectors.find(
                (c) => c.id === connectionModal.connector?.id,
              );

              if (connector?.isConnected) {
                // Connection was successful, just update the UI
                setConnectors(updatedConnectors);
                setOauthFlow({ state: "idle", connector: null });
                setSnackbar({
                  open: true,
                  message: t("oauth.connection_established_success"),
                  severity: "success",
                });
                callbackHandledRef.current = true;
              } else {
                // Popup was closed without completing authorization
                setOauthFlow({
                  state: "error",
                  connector: null,
                  error: t("oauth.authorization_cancelled"),
                });
              }
            } catch (err) {
              // If we can't check, assume it was cancelled
              setOauthFlow({
                state: "error",
                connector: null,
                error: t("oauth.authorization_cancelled"),
              });
            }
          };

          // Small delay to allow postMessage to arrive first
          setTimeout(checkConnectionStatus, 500);
        }
      }, 1000);
    } catch (error) {
      setOauthFlow({
        state: "error",
        connector: null,
        error:
          error instanceof Error
            ? error.message
            : t("oauth.authorization_failed"),
      });
    }
  };

  const handleTestConnection = async (connector: ConnectorWithStatus) => {
    if (!connector.connection) return;

    setTestingConnection(connector.id);
    try {
      const result = await testConnection(connector.id);
      if (result.success) {
        setSnackbar({
          open: true,
          message: t("oauth.connection_test_successful"),
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: t("oauth.connection_test_failed", { error: result.error }),
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("oauth.connection_test_failed", { error: "" }),
        severity: "error",
      });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDisconnect = async () => {
    if (!selectedConnection) return;

    try {
      await disconnectConnector(selectedConnection.id);
      setSnackbar({
        open: true,
        message: t("oauth.connections.disconnected_success"),
        severity: "success",
      });
      setAnchorEl(null);
      setSelectedConnection(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("oauth.connections.disconnect_failed"),
        severity: "error",
      });
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    connector: ConnectorWithStatus,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedConnection(connector);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConnection(null);
  };

  // Webhook handlers
  const handleToggleWebhooks = async (connector: ConnectorWithStatus) => {
    const connectionId = connector.connection?.id;
    const connectorId = connector.id;

    if (!connectionId || !connectorId) return;

    const isCurrentlyExpanded = expandedWebhooks[connectionId];

    setExpandedWebhooks((prev) => ({
      ...prev,
      [connectionId]: !isCurrentlyExpanded,
    }));

    // Fetch subscriptions if expanding and not yet loaded
    if (!isCurrentlyExpanded && !webhookSubscriptions[connectionId]) {
      setLoadingWebhooks((prev) => ({ ...prev, [connectionId]: true }));
      try {
        const subs = await getWebhookSubscriptions(connectorId);
        setWebhookSubscriptions((prev) => ({ ...prev, [connectionId]: subs }));
      } catch (error) {
        logger.error("Failed to fetch webhook subscriptions:", error);
        setSnackbar({
          open: true,
          message: t("oauth.webhooks.failed_load"),
          severity: "error",
        });
      } finally {
        setLoadingWebhooks((prev) => ({ ...prev, [connectionId]: false }));
      }
    }
  };

  const handlePauseWebhook = async (
    subscriptionId: string,
    connectionId: string,
    connectorId: string,
  ) => {
    try {
      await pauseWebhookSubscription(subscriptionId);
      // Refresh subscriptions
      const subs = await getWebhookSubscriptions(connectorId);
      setWebhookSubscriptions((prev) => ({ ...prev, [connectionId]: subs }));
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.paused"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.pause_failed"),
        severity: "error",
      });
    }
  };

  const handleResumeWebhook = async (
    subscriptionId: string,
    connectionId: string,
    connectorId: string,
  ) => {
    try {
      await resumeWebhookSubscription(subscriptionId);
      // Refresh subscriptions
      const subs = await getWebhookSubscriptions(connectorId);
      setWebhookSubscriptions((prev) => ({ ...prev, [connectionId]: subs }));
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.resumed"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.resume_failed"),
        severity: "error",
      });
    }
  };

  const handleRenewWebhook = async (
    subscriptionId: string,
    connectionId: string,
    connectorId: string,
  ) => {
    try {
      await renewWebhookSubscription(subscriptionId);
      // Refresh subscriptions
      const subs = await getWebhookSubscriptions(connectorId);
      setWebhookSubscriptions((prev) => ({ ...prev, [connectionId]: subs }));
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.renewed"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.renew_failed"),
        severity: "error",
      });
    }
  };

  const handleDeleteWebhook = async (
    subscriptionId: string,
    connectionId: string,
    connectorId: string,
  ) => {
    if (!window.confirm(t("oauth.webhooks.confirm_delete"))) {
      return;
    }

    try {
      await deleteWebhookSubscription(subscriptionId);
      // Refresh subscriptions
      const subs = await getWebhookSubscriptions(connectorId);
      setWebhookSubscriptions((prev) => ({ ...prev, [connectionId]: subs }));
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.deleted"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("oauth.webhooks.delete_failed"),
        severity: "error",
      });
    }
  };

  const formatScopes = (scopes: string[]): string[] => {
    return scopes.map((scope) => {
      // Extract readable permission from scope
      if (scope.includes("gmail")) return t("oauth.scopes.gmail_access");
      if (scope.includes("calendar")) return t("oauth.scopes.calendar_access");
      if (scope.includes("channels:read"))
        return t("oauth.scopes.channels_read");
      if (scope.includes("chat:write")) return t("oauth.scopes.chat_write");
      if (scope.includes("users:read")) return t("oauth.scopes.users_read");
      if (scope.includes("mail.read")) return t("oauth.scopes.mail_read");
      return scope.split("/").pop() || scope;
    });
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          height: "100vh",
          overflowY: "auto",
          background: isDark
            ? "linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)"
            : "#EEEEEF",
          p: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: "column",
          paddingTop: { xs: "60px", md: "80px" }, // Altura da navbar (mobile: 60px, desktop: 80px)
        }}
      >
        {/* Responsive Navbar */}
        <ResponsiveNavbar
          isConnected={false}
          connection={{ status: "disconnected", message: "" }}
          isDarkTheme={isDarkTheme}
          onThemeToggle={toggleTheme}
          isSidebarCollapsed={false}
          profileImage={
            agentConnectionStatus?.isAgentConnected
              ? jessProfileIcon
              : jessProfileIconOff
          }
          profileName={jessNameLogo}
          agentConnectionStatus={agentConnectionStatus}
        />
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            width: "100%",
            mt: { xs: 3, md: 4 },
          }}
        >
          {/* Header with improved design */}
          <Box
            sx={{
              background: cardBg,
              border: cardBorder,
              borderRadius: 4,
              p: 4,
              mb: 4,
              boxShadow: isDark
                ? "0 4px 16px rgba(0,0,0,0.4)"
                : "0 2px 8px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              }}
            >
              <LinkIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: textPrimary,
                mb: 1.5,
                fontSize: "1.8rem",
              }}
            >
              {t("connections")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: textSecondary,
                maxWidth: 600,
                mx: "auto",
                fontWeight: 300,
                fontSize: "1rem",
              }}
            >
              {t("oauth.connections.description")}
            </Typography>
          </Box>

          {/* Connectors Grid */}
          {loading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 3,
              }}
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <Card
                  key={index}
                  sx={{ background: cardBg, border: cardBorder }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Skeleton variant="circular" width={56} height={56} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="text" width="100%" height={40} />
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={36}
                      sx={{ mt: 2 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : connectors.length === 0 ? (
            <Box
              sx={{
                background: cardBg,
                border: cardBorder,
                borderRadius: 4,
                p: 6,
                textAlign: "center",
                boxShadow: isDark
                  ? "0 4px 16px rgba(0,0,0,0.4)"
                  : "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 4,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                }}
              >
                <ExtensionIcon sx={{ fontSize: 60, color: "white" }} />
              </Box>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  color: textPrimary,
                  fontWeight: 600,
                  mb: 2,
                  fontSize: "1.5rem",
                }}
              >
                {t("oauth.connections.no_connectors")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: textSecondary,
                  maxWidth: 400,
                  mx: "auto",
                  mb: 3,
                  fontWeight: 300,
                  fontSize: "1rem",
                }}
              >
                {t("oauth.connections.contact_admin")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  mt: 4,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: textMuted,
                    fontStyle: "italic",
                  }}
                >
                  {t("oauth.connections.popular_integrations")}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 3,
                alignItems: "start",
              }}
            >
              {connectors.map((connector) => (
                <Card
                  key={connector.id}
                  sx={{
                    position: "relative",
                    background: cardBg,
                    border: cardBorder,
                    borderRadius: 3,
                    transition: "all 0.3s ease",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "280px",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: isDark
                        ? "0 8px 32px rgba(0,0,0,0.5)"
                        : "0 8px 24px rgba(0, 0, 0, 0.15)",
                    },
                    opacity: connector.status === "inactive" ? 0.6 : 1,
                    boxShadow: isDark
                      ? "0 4px 16px rgba(0,0,0,0.4)"
                      : "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <CardContent
                    sx={{
                      pb: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Avatar
                        src={connector.image_url}
                        alt={connector.name}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: connector.image_url
                            ? "transparent"
                            : theme.palette.primary.main,
                        }}
                      >
                        {!connector.image_url && connector.name.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          noWrap
                          sx={{
                            color: textPrimary,
                            fontWeight: 600,
                            fontSize: "1.1rem",
                          }}
                        >
                          {getConnectionDisplayName(connector)}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            icon={
                              connector.isConnected ? (
                                <CheckCircleIcon />
                              ) : undefined
                            }
                            label={
                              connector.isConnected
                                ? t("oauth.connections.connected")
                                : t("oauth.connections.available")
                            }
                            color={
                              connector.isConnected ? "success" : "default"
                            }
                            size="small"
                          />
                          {connector.connection?.status === "expired" && (
                            <Chip
                              icon={<ErrorIcon />}
                              label={t("oauth.connections.expired")}
                              color="warning"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                      {connector.isConnected && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, connector)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 3,
                        color: textSecondary,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "40px",
                      }}
                    >
                      {connector.description}
                    </Typography>

                    {/* Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexDirection: "column",
                        mt: "auto",
                      }}
                    >
                      {connector.isConnected ? (
                        <>
                          <Button
                            variant="outlined"
                            startIcon={
                              testingConnection === connector.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RefreshIcon />
                              )
                            }
                            onClick={() => handleTestConnection(connector)}
                            disabled={testingConnection === connector.id}
                            fullWidth
                            sx={{ borderColor: "#667eea", color: "#667eea" }}
                          >
                            {t("oauth.connections.test_connection")}
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              handleAddAnotherConnection(connector)
                            }
                            fullWidth
                            sx={{
                              borderColor: "#34A853",
                              color: "#34A853",
                              mt: 1,
                            }}
                          >
                            {t("oauth.connections.add_another")}
                          </Button>
                          {connector.connection?.expires_at && (
                            <Typography
                              variant="caption"
                              textAlign="center"
                              sx={{ color: textMuted }}
                            >
                              {t("oauth.connections.expires_at", {
                                date: formatLocaleDate(
                                  connector.connection.expires_at,
                                  i18n.language,
                                ),
                              })}
                            </Typography>
                          )}

                          {/* Webhook Subscriptions Section */}
                          {connector.webhook_config?.enabled &&
                            connector.connection && (
                              <>
                                <Divider
                                  sx={{
                                    my: 1,
                                    borderColor: isDark
                                      ? "rgba(255,255,255,0.1)"
                                      : undefined,
                                  }}
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "pointer",
                                    py: 0.5,
                                  }}
                                  onClick={() =>
                                    handleToggleWebhooks(connector)
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <WebhookIcon
                                      sx={{ fontSize: 16, color: "#667eea" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600, color: "#667eea" }}
                                    >
                                      {t("oauth.webhooks.title")}
                                    </Typography>
                                  </Box>
                                  <ExpandMoreIcon
                                    sx={{
                                      fontSize: 18,
                                      transform: expandedWebhooks[
                                        connector.connection.id
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                      color: "#667eea",
                                    }}
                                  />
                                </Box>
                                <Collapse
                                  in={expandedWebhooks[connector.connection.id]}
                                >
                                  <Box sx={{ pt: 1 }}>
                                    {loadingWebhooks[
                                      connector.connection.id
                                    ] ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                          py: 1,
                                        }}
                                      >
                                        <CircularProgress size={20} />
                                      </Box>
                                    ) : webhookSubscriptions[
                                        connector.connection.id
                                      ]?.length > 0 ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        {webhookSubscriptions[
                                          connector.connection.id
                                        ].map((sub) => (
                                          <Box
                                            key={sub.id}
                                            sx={{
                                              p: 1,
                                              borderRadius: 1,
                                              bgcolor: isDark
                                                ? "rgba(102, 126, 234, 0.1)"
                                                : "rgba(102, 126, 234, 0.05)",
                                              border: isDark
                                                ? "1px solid rgba(102, 126, 234, 0.25)"
                                                : "1px solid rgba(102, 126, 234, 0.1)",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 0.5,
                                              }}
                                            >
                                              <Chip
                                                label={sub.status}
                                                size="small"
                                                color={getWebhookStatusColor(
                                                  sub.status,
                                                )}
                                                sx={{
                                                  height: 20,
                                                  fontSize: "0.65rem",
                                                }}
                                              />
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {sub.status === "active" ? (
                                                  <Tooltip
                                                    title={t(
                                                      "oauth.webhooks.pause",
                                                    )}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePauseWebhook(
                                                          sub.id,
                                                          connector.connection!
                                                            .id,
                                                          connector.id,
                                                        );
                                                      }}
                                                      sx={{ p: 0.25 }}
                                                    >
                                                      <PauseIcon
                                                        sx={{ fontSize: 14 }}
                                                      />
                                                    </IconButton>
                                                  </Tooltip>
                                                ) : sub.status === "paused" ? (
                                                  <Tooltip
                                                    title={t(
                                                      "oauth.webhooks.resume",
                                                    )}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleResumeWebhook(
                                                          sub.id,
                                                          connector.connection!
                                                            .id,
                                                          connector.id,
                                                        );
                                                      }}
                                                      sx={{ p: 0.25 }}
                                                    >
                                                      <PlayArrowIcon
                                                        sx={{ fontSize: 14 }}
                                                      />
                                                    </IconButton>
                                                  </Tooltip>
                                                ) : null}
                                                {(sub.status === "expired" ||
                                                  sub.status === "active") && (
                                                  <Tooltip
                                                    title={t(
                                                      "oauth.webhooks.renew",
                                                    )}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRenewWebhook(
                                                          sub.id,
                                                          connector.connection!
                                                            .id,
                                                          connector.id,
                                                        );
                                                      }}
                                                      sx={{ p: 0.25 }}
                                                    >
                                                      <AutorenewIcon
                                                        sx={{ fontSize: 14 }}
                                                      />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                <Tooltip
                                                  title={t(
                                                    "oauth.webhooks.delete",
                                                  )}
                                                >
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteWebhook(
                                                        sub.id,
                                                        connector.connection!
                                                          .id,
                                                        connector.id,
                                                      );
                                                    }}
                                                    sx={{
                                                      p: 0.25,
                                                      color: "error.main",
                                                    }}
                                                  >
                                                    <DeleteIcon
                                                      sx={{ fontSize: 14 }}
                                                    />
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            </Box>
                                            {sub.email && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: textPrimary,
                                                  display: "block",
                                                  fontWeight: 500,
                                                  mb: 0.5,
                                                }}
                                              >
                                                {sub.email}
                                              </Typography>
                                            )}
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: textSecondary,
                                                display: "block",
                                              }}
                                            >
                                              {sub.event_types
                                                .slice(0, 2)
                                                .map(formatWebhookEventType)
                                                .join(", ")}
                                              {sub.event_types.length > 2 &&
                                                ` +${sub.event_types.length - 2}`}
                                            </Typography>
                                            {sub.expires_at && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: textMuted,
                                                  fontSize: "0.65rem",
                                                }}
                                              >
                                                {t("oauth.webhooks.expires")}:{" "}
                                                {formatLocaleDate(
                                                  sub.expires_at,
                                                  i18n.language,
                                                )}
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: textMuted,
                                          textAlign: "center",
                                          display: "block",
                                        }}
                                      >
                                        {t("oauth.webhooks.no_subscriptions")}
                                      </Typography>
                                    )}
                                  </Box>
                                </Collapse>
                              </>
                            )}
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<LaunchIcon />}
                          onClick={() => handleConnect(connector)}
                          disabled={connector.status === "inactive"}
                          fullWidth
                          sx={{
                            background:
                              "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
                          }}
                        >
                          {t("oauth.connections.connect")}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Connection Modal */}
          <Dialog
            open={connectionModal.open}
            onClose={() => setConnectionModal({ open: false, connector: null })}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                bgcolor: dialogBg,
                border: dialogBorder,
                color: isDark ? "#fff" : undefined,
              },
            }}
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {connectionModal.connector?.image_url ? (
                <Avatar
                  src={connectionModal.connector.image_url}
                  alt={connectionModal.connector.name}
                  sx={{ width: 40, height: 40 }}
                />
              ) : (
                <SecurityIcon color="primary" />
              )}
              {t("oauth.connections.connect_to", {
                name: connectionModal.connector?.name,
              })}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph>
                {connectionModal.connector?.description}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                {t("oauth.connections.permissions_title")}
              </Typography>

              <List dense>
                {connectionModal.connector &&
                  formatScopes(connectionModal.connector.scopes).map(
                    (permission, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <VpnKeyIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={permission} />
                      </ListItem>
                    ),
                  )}
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                {t("oauth.connections.redirect_notice", {
                  name: connectionModal.connector?.name,
                })}
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setConnectionModal({ open: false, connector: null })
                }
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={handleStartAuth}
              >
                {t("oauth.connections.authorize")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* OAuth Flow Backdrop */}
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              background:
                "linear-gradient(135deg, rgba(52, 168, 83, 0.95) 0%, rgba(113, 200, 170, 0.95) 100%)",
            }}
            open={
              oauthFlow.state === "authorizing" || oauthFlow.state === "waiting"
            }
          >
            <Box
              sx={{
                textAlign: "center",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                borderRadius: 4,
                p: 4,
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                minWidth: "300px",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  animation: "pulse 2s ease-in-out infinite",
                }}
              >
                <CircularProgress color="inherit" size={32} thickness={4} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {oauthFlow.state === "authorizing"
                  ? t("oauth.preparing_authorization")
                  : t("oauth.waiting_for_authorization")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.9, lineHeight: 1.5 }}
              >
                {oauthFlow.state === "waiting"
                  ? t("oauth.complete_in_popup")
                  : connectionModal.connector
                    ? `${connectionModal.connector.name} ${t("oauth.redirecting_back")}`
                    : ""}
              </Typography>
              {oauthFlow.state === "waiting" && (
                <Typography
                  variant="caption"
                  sx={{ mt: 2, display: "block", opacity: 0.7 }}
                >
                  {t("oauth.popup_blocked_help", {
                    default:
                      "If popup is blocked, please allow popups and try again",
                  })}
                </Typography>
              )}
            </Box>
            <style>{`
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
            }
            50% {
              box-shadow: 0 0 0 20px rgba(255, 255, 255, 0);
            }
          }
        `}</style>
          </Backdrop>

          {/* Connection Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                bgcolor: dialogBg,
                border: dialogBorder,
                color: isDark ? "#fff" : undefined,
              },
            }}
          >
            <MenuItem
              onClick={() =>
                selectedConnection && handleTestConnection(selectedConnection)
              }
            >
              <RefreshIcon sx={{ mr: 1, fontSize: 20 }} />
              {t("oauth.connections.test_connection")}
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedConnection &&
                handleAddAnotherConnection(selectedConnection)
              }
            >
              <AddIcon sx={{ mr: 1, fontSize: 20 }} />
              {t("oauth.connections.add_another")}
            </MenuItem>
            <MenuItem onClick={handleDisconnect}>
              <CloseIcon sx={{ mr: 1, fontSize: 20 }} />
              {t("oauth.connections.disconnect")}
            </MenuItem>
          </Menu>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Atlassian Site Selection Dialog */}
          <Dialog
            open={siteSelectionDialog.open}
            onClose={() =>
              !siteSelectionDialog.loading &&
              setSiteSelectionDialog({
                open: false,
                connectionId: "",
                resources: [],
                loading: false,
              })
            }
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                bgcolor: dialogBg,
                border: dialogBorder,
                color: isDark ? "#fff" : undefined,
              },
            }}
          >
            <DialogTitle>
              {t("oauth.connections.select_site") || "Select Atlassian Site"}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("oauth.connections.select_site_description") ||
                  "Your account has access to multiple Atlassian sites. Please select which site you want to connect."}
              </Typography>
              <List>
                {siteSelectionDialog.resources.map((resource) => (
                  <ListItem
                    key={resource.id}
                    component="div"
                    onClick={() =>
                      !siteSelectionDialog.loading &&
                      handleSelectSite(resource.id)
                    }
                    sx={{
                      cursor: siteSelectionDialog.loading
                        ? "default"
                        : "pointer",
                      borderRadius: 1,
                      mb: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: siteSelectionDialog.loading
                          ? "transparent"
                          : "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <LaunchIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={resource.name}
                      secondary={resource.url}
                    />
                  </ListItem>
                ))}
              </List>
              {siteSelectionDialog.loading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setSiteSelectionDialog({
                    open: false,
                    connectionId: "",
                    resources: [],
                    loading: false,
                  })
                }
                disabled={siteSelectionDialog.loading}
              >
                {t("cancel")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* OAuth Error Dialog */}
          <Dialog
            open={oauthFlow.state === "error"}
            onClose={() => setOauthFlow({ state: "idle", connector: null })}
            PaperProps={{
              sx: {
                bgcolor: dialogBg,
                border: dialogBorder,
                color: isDark ? "#fff" : undefined,
              },
            }}
          >
            <DialogTitle>{t("oauth.authorization_failed")}</DialogTitle>
            <DialogContent>
              <Typography>
                {oauthFlow.error || t("oauth.error_occurred_authorization")}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOauthFlow({ state: "idle", connector: null })}
              >
                {t("ok")}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </>
  );
};

export default ConnectionsPage;
